// src/utils/errorHandler.js

/**
 * Centralized Error Handling Utilities
 * 
 * Provides consistent error handling, classification, and user-friendly messages
 * across the application. Includes logging, error categorization, and retry logic.
 */

/**
 * Error types for classification
 */
export const ErrorType = {
  NETWORK: 'NETWORK',
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORIZATION: 'AUTHORIZATION',
  VALIDATION: 'VALIDATION',
  NOT_FOUND: 'NOT_FOUND',
  SERVER: 'SERVER',
  TIMEOUT: 'TIMEOUT',
  RATE_LIMIT: 'RATE_LIMIT',
  UNKNOWN: 'UNKNOWN'
};

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  LOW: 'LOW',       // Informational, doesn't block user
  MEDIUM: 'MEDIUM', // Blocks current operation but user can continue
  HIGH: 'HIGH',     // Blocks feature/section
  CRITICAL: 'CRITICAL' // Blocks entire application
};

/**
 * Classify error by type based on error object
 * @param {Error|object} error - Error object or Axios error
 * @returns {string} Error type from ErrorType enum
 */
export const classifyError = (error) => {
  // Network errors (no response from server)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return ErrorType.TIMEOUT;
    }
    return ErrorType.NETWORK;
  }

  // HTTP status code based classification
  const status = error.response?.status;
  
  if (status === 401) return ErrorType.AUTHENTICATION;
  if (status === 403) return ErrorType.AUTHORIZATION;
  if (status === 404) return ErrorType.NOT_FOUND;
  if (status === 422 || status === 400) return ErrorType.VALIDATION;
  if (status === 429) return ErrorType.RATE_LIMIT;
  if (status >= 500) return ErrorType.SERVER;
  
  return ErrorType.UNKNOWN;
};

/**
 * Get error severity based on type
 * @param {string} errorType - Error type from ErrorType enum
 * @returns {string} Severity level
 */
export const getErrorSeverity = (errorType) => {
  switch (errorType) {
    case ErrorType.AUTHENTICATION:
    case ErrorType.NETWORK:
      return ErrorSeverity.CRITICAL;
    
    case ErrorType.AUTHORIZATION:
    case ErrorType.SERVER:
      return ErrorSeverity.HIGH;
    
    case ErrorType.TIMEOUT:
    case ErrorType.RATE_LIMIT:
      return ErrorSeverity.MEDIUM;
    
    case ErrorType.VALIDATION:
    case ErrorType.NOT_FOUND:
      return ErrorSeverity.LOW;
    
    default:
      return ErrorSeverity.MEDIUM;
  }
};

/**
 * Get user-friendly error message
 * @param {Error|object} error - Error object
 * @param {function} t - i18next translation function
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyMessage = (error, t) => {
  const errorType = classifyError(error);
  
  // Try to get custom error message from API response
  const customMessage = error.response?.data?.message || 
                       error.response?.data?.error ||
                       error.response?.data?.title;
  
  // If API provides a message, use it (but sanitize)
  if (customMessage && typeof customMessage === 'string') {
    return customMessage;
  }
  
  // Fallback to generic messages based on error type
  const defaultMessages = {
    [ErrorType.NETWORK]: t?.('errors.networkError') || 'Network error. Please check your connection.',
    [ErrorType.AUTHENTICATION]: t?.('errors.unauthorized') || 'Please log in to continue.',
    [ErrorType.AUTHORIZATION]: t?.('errors.forbidden') || 'You don\'t have permission to perform this action.',
    [ErrorType.VALIDATION]: t?.('errors.validation') || 'Please check your input and try again.',
    [ErrorType.NOT_FOUND]: t?.('errors.notFound') || 'The requested resource was not found.',
    [ErrorType.SERVER]: t?.('errors.serverError') || 'Server error. Please try again later.',
    [ErrorType.TIMEOUT]: t?.('errors.timeout') || 'Request timed out. Please try again.',
    [ErrorType.RATE_LIMIT]: 'Too many requests. Please wait a moment and try again.',
    [ErrorType.UNKNOWN]: t?.('errors.general') || 'An error occurred. Please try again.'
  };
  
  return defaultMessages[errorType] || defaultMessages[ErrorType.UNKNOWN];
};

/**
 * Check if error is retryable
 * @param {Error|object} error - Error object
 * @returns {boolean} Whether the error can be retried
 */
export const isRetryableError = (error) => {
  const errorType = classifyError(error);
  
  // Retryable error types
  const retryableTypes = [
    ErrorType.NETWORK,
    ErrorType.TIMEOUT,
    ErrorType.SERVER,
    ErrorType.RATE_LIMIT
  ];
  
  return retryableTypes.includes(errorType);
};

/**
 * Log error with context
 * @param {Error|object} error - Error object
 * @param {object} context - Additional context (component, action, etc.)
 */
export const logError = (error, context = {}) => {
  const errorType = classifyError(error);
  const severity = getErrorSeverity(errorType);
  
  const errorInfo = {
    type: errorType,
    severity,
    message: error.message,
    status: error.response?.status,
    url: error.config?.url,
    method: error.config?.method,
    timestamp: new Date().toISOString(),
    ...context
  };
  
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('[Error Handler]', errorInfo, error);
  }
  
  // In production, send to monitoring service (e.g., Application Insights, Sentry)
  if (process.env.NODE_ENV === 'production') {
    // TODO: Send to Application Insights or error tracking service
    // Example: window.appInsights?.trackException({ exception: error, properties: errorInfo });
  }
  
  return errorInfo;
};

/**
 * Retry configuration
 */
export const RetryConfig = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000,    // 10 seconds
  backoffMultiplier: 2
};

/**
 * Calculate retry delay with exponential backoff
 * @param {number} retryCount - Current retry attempt (0-indexed)
 * @returns {number} Delay in milliseconds
 */
export const calculateRetryDelay = (retryCount) => {
  const delay = RetryConfig.initialDelay * Math.pow(RetryConfig.backoffMultiplier, retryCount);
  return Math.min(delay, RetryConfig.maxDelay);
};

/**
 * Retry a failed operation with exponential backoff
 * @param {function} operation - Async function to retry
 * @param {object} options - Retry options
 * @returns {Promise} Result of successful operation
 */
export const retryOperation = async (
  operation,
  options = {}
) => {
  const {
    maxRetries = RetryConfig.maxRetries,
    shouldRetry = isRetryableError,
    onRetry = () => {}
  } = options;
  
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry if this is the last attempt or error is not retryable
      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }
      
      // Calculate delay and wait before retrying
      const delay = calculateRetryDelay(attempt);
      
      // Notify about retry
      onRetry(attempt + 1, delay, error);
      
      // Log retry attempt
      logError(error, {
        action: 'retry',
        attempt: attempt + 1,
        maxRetries,
        nextRetryDelay: delay
      });
      
      // Wait before next retry
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

/**
 * Create a retry wrapper for API calls
 * @param {function} apiCall - API function to wrap
 * @param {object} retryConfig - Custom retry configuration
 * @returns {function} Wrapped API function with retry logic
 */
export const withRetry = (apiCall, retryConfig = {}) => {
  return async (...args) => {
    return retryOperation(
      () => apiCall(...args),
      retryConfig
    );
  };
};

/**
 * Error handler utility class
 */
export class ErrorHandler {
  constructor(t) {
    this.t = t; // i18next translation function
  }
  
  /**
   * Handle error and return formatted error info
   */
  handle(error, context = {}) {
    const errorType = classifyError(error);
    const severity = getErrorSeverity(errorType);
    const message = getUserFriendlyMessage(error, this.t);
    const isRetryable = isRetryableError(error);
    const errorInfo = logError(error, context);
    
    return {
      type: errorType,
      severity,
      message,
      isRetryable,
      originalError: error,
      ...errorInfo
    };
  }
  
  /**
   * Handle error with retry logic
   */
  async handleWithRetry(operation, options = {}) {
    try {
      return await retryOperation(operation, {
        ...options,
        onRetry: (attempt, delay, error) => {
          console.log(`Retrying (${attempt}/${options.maxRetries || RetryConfig.maxRetries})...`);
          options.onRetry?.(attempt, delay, error);
        }
      });
    } catch (error) {
      return this.handle(error, options.context);
    }
  }
}

export default ErrorHandler;
